import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PaywallContent from './paywall-content'
import PaywallStructuredData from './paywall-structured-data'

import { cn } from '@/shared/utils'
import { postAmplitude } from '@/core/lib'
import { useLocales } from '@/shared/hooks/useLocales'

export interface PaywallOverlayProps {
  /** Paywall 표시 여부 */
  isVisible: boolean
  /** Paywall를 닫을 때 호출되는 콜백 */
  onClose?: () => void
  /** 페이지 타입 (analytics용) */
  pageType?: 'community' | 'post' | 'column' | 'tip'
  /** 콘텐츠 ID (analytics용) */
  contentId?: string | number
  /** 콘텐츠 카테고리 (analytics용) */
  contentCategory?: string
  /** 추가 CSS 클래스 */
  className?: string
  /** 닫기 버튼 표시 여부 */
  isCloseButtonVisible?: boolean
}

const PaywallOverlay: FC<PaywallOverlayProps> = ({
  isVisible,
  onClose,
  pageType = 'community',
  contentId,
  contentCategory,
  isCloseButtonVisible,
  className,
}) => {
  const { t } = useTranslation()
  const { locale } = useLocales()
  const [isAnimating, setIsAnimating] = useState(false)

  // Analytics 이벤트 추적
  useEffect(() => {
    if (isVisible) {
      postAmplitude('view_paywall', {
        page_type: pageType,
        content_id: contentId,
        content_category: contentCategory,
        language: locale,
      })
    }
  }, [isVisible, pageType, contentId, contentCategory, locale])

  // Paywall 닫기 처리
  const handleClose = () => {
    postAmplitude('paywall_dismissed', {
      page_path: window.location.pathname,
      language: locale,
    })

    onClose?.()
  }

  if (!isVisible) {
    return null
  }

  return (
    <>
      {/* SEO용 구조화된 데이터 */}
      <PaywallStructuredData
        pageType={pageType}
        locale={locale}
        contentId={contentId}
        contentCategory={contentCategory}
      />
      {/* 오버레이 배경 */}
      <div
        className={cn(
          'fixed inset-0 z-[300] flex items-center justify-center',
          'bg-white bg-opacity-50 backdrop-blur-sm',
          isAnimating && 'animate-fadeOut',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-title"
        aria-describedby="paywall-description"
      >
        {/* 바텀 시트를 사용한 메인 콘텐츠 */}
        <div
          className={cn(
            'w-[360px] max-w-[90vw] relative overflow-y-auto rounded-2xl shadow-lg',
            isAnimating && 'animate-slideDown',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 닫기 버튼 */}
          {isCloseButtonVisible && (
            <button
              onClick={handleClose}
              className={cn(
                'absolute top-4 right-4 z-10',
                'w-8 h-8 flex items-center justify-center',
                'rounded-full bg-gray-100 hover:bg-gray-200',
                'text-gray-600 hover:text-gray-800',
                'transition-colors duration-200',
              )}
              aria-label={t('common.close')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Paywall 콘텐츠 */}
          <PaywallContent />
        </div>
      </div>
    </>
  )
}

export default PaywallOverlay
